import dayjs from 'dayjs';
import i18n from 'i18n';
import type { Dictionary } from 'lodash';
import { every, get, groupBy, isObject, keyBy, uniqBy, last } from 'lodash';
import type { Location } from 'react-router';
import { type TableState } from 'store/tableState/table.state.types';
import type { IExpenseV2 } from 'types/expenses.types';
import type {
  LockStatusType,
  LockStatusTypeCatgoriesed,
  Nullable,
  OptionType,
  QP,
} from 'types/global.types';
import type { IncomeTaxDE } from 'types/incomeTax.types';
import { InvoiceTypeEnum, type Invoice } from 'types/invoice.types';
import type { TaxesByYear } from 'types/taxes.types';
import { CivilStatusEnum } from 'types/taxes.types';
import {
  AvailableCountries,
  AvailableLanguages,
  VATTypeEnum,
  type IUser,
} from 'types/users.types';
import { isExpert, isWeb } from './constants';

export const formatTableState = (tableState: TableState) => {
  return {
    ...tableState,
    meta: {
      ...tableState.meta,
      filtersData: {
        ...tableState.meta.filtersData,
        text:
          tableState.meta.filtersData.text?.length > 1
            ? tableState.meta.filtersData.text
            : undefined,
      },
    },
  };
};

const chiftACL = [
  'rca@mycatalyst.eu',
  'Mike@fiskcouncil.be',
  'giraud@efficiens.be',
  'david@my-cfo.be',
  'Wannes@2ug.be',
  'charlene@kantoordewulf.be',
  'christophe@meesters.be',
  'koen@makofisc.be',
  'demaconsultancy@outlook.com',
  'info@kantoordewulf.be',
  'itservices@bakertilly.be',
  'wannes@2ug.be',
  'info@gsv-compta.be',
  'sylvain.lamant@excofi.be',
  's.elk@jmstaxaudit.be',
  'diana.gulger@bdo.be',
  'virginie.lopezfertinel@bdo.be',
  'patryk.olszynski@bdo.be',
  'ict@titeca.be',
  'matthieu.hertoghe@chift.eu',
  'marie-julie.vanoosthuyse@titeca.be',
  'diana.azevedoloureiro@titeca.be',
  'maxime.deveen@gmail.com',
  'nickanno@gmail.com',
  'stephytrue@gmail.com',
  'gurkancinoglu332@gmail.com',
  'wouter_hautekeur@hotmail.com',
  'leander.hoedt@gmail.com',
  'knops.stephanie@gmail.com',
  'vanhoutte.michiel@hotmail.be',
  'michiel@degheest.com',
  'info@sakpellens.be',
  'geert.de.bruycker@skynet.be',
  'michael@fisconte.be',
  'alexander@centum.be',
  'info@svandebosch.be',
  'jeremy.wauters@bcvw.be',
  'info@degefinance.com',
  'info@jpconsulting.be',
  'brecht@raadhuis.be',
  'kelly@bokesi.be',
  'jean@jl-compta.be',
  'info@sakpellens.be',
  'ilse@fiskabo.be',
  'dirk@lucclaes.be',
  'brecht@raadhuis.be',
  'info@jpconsulting.be',
  'info@degefinance.com',
  'jeremy.wauters@bcvw.be',
  'info@svandebosch.be',
  'h-spam@outlook.de',
  'alexander@centum.be',
  'michael@fisconte.be',
  'info@fiscaldo.be',
  'drough1365@gmail.com',
  'aschoolmeesters@vcsa.be',
  'geert@gvoconsult.be',
  'krist@hetaccountantskantoor.be',
  'consulting.wisely@gmail.com',
  'krist@hetaccountantskantoor.be',
  'ub@fiscaconseils.be',
  'cmmprensa0@gmail.com',
  'ann@devuyst.biz',
  'info@gmdconsulting.be',
  'valerie@fdlo.be',
  'nathalielauwereyns@gmail.com',
  'sc@socofidex.be',
  'alina.grincel@littlegrinoffice.be',
  'rousseau.seppe@gmail.com',
  'junotroegiers@gmail.com',
  'profconsult@outlook.be',
  'ac@fidem.be',
  'jeroen.defoort@mamboconsulting.be',
  'lindsay.thiry@outlook.com',
  'office@fiducon.be',
  'info@fiscaldo.be',
  'bjorn@projecthaaltertvzw.be',
  'accountable@fidjconsulting.com',
  'kmo.accounting@telenet.be',
  'vr@fufisco.be',
  'michel.vercammen@fiduciairehupperetz.be',
  'administratie@battlebranding.be',
  'benjamin.engelen@cbfisc.be',
  'info@bultijnck.be',
  'wouter@wvgconsult.be',
  'amine.el@fiduciaconsulting.be',
  'jtheunis@skynet.be',
  'cs@fidsoreco.be',
  'compta@laurentcardon.be',
  'gw.financial.management@gmail.com',
  'kadir@kadirkaya.be',
  'marijke@bultijnck.be',
  'mgonzalez@fiscaliege.be',
  'bert@actes.be',
  'cedric@darvil.be',
  'nligot@darvil.be',
  'neytstef@gmail.com',
  'schippers.s@ibicore.be',
  'logins@acs.be',
  'laurent.oosterlinck@vgd.eu',
  'info@pvaconsulting.be',
  'gwendoline.mortier@haentjens.biz',
  'dennis@i-count.be',
  'jja@bfs.be',
];

const shouldSeeChiftIntegrations = (email: string) =>
  isExpert &&
  (email.includes('@accountable.eu') ||
    email.includes('@mycatalyst.eu') ||
    email.includes('@chift.eu') ||
    email.includes('@fiskcouncil.be') ||
    email.includes('@my-cfo.be') ||
    email.includes('@ghconsulting.eu') ||
    email.includes('@jacques-associes.be') ||
    email.includes('@fagconsult.be') ||
    email.includes('@mh-accountancy.be') ||
    email.includes('@edufisc.be') ||
    email.includes('@fiscopti.be') ||
    email.includes('@mayalasconsulting.com') ||
    email.includes('@adfisc.be') ||
    email.includes('@4thman.be') ||
    chiftACL.includes(email));

const appendSearchParamsToURL = (
  location: Location,
  params: Record<string, any>,
): string => {
  const { pathname, search } = location;
  const url = new URL(window.location.origin + pathname + search);

  Object.entries(params).forEach(([key, value]: [key: string, value: any]) => {
    url.searchParams.set(key, value);
  });

  return url.pathname + url.search;
};

const taxOfficeRegex =
  /SPF|FOD|FOF|Service Public Fédéral Finances|Federale Overheidsdienst Financiën|Föderaler Öffentlicher Dienst Finanzen/i;

const isTaxOfficeName = (counterpartyName: string) =>
  taxOfficeRegex.test(counterpartyName);

const removeNonAlphanumeric = (value = '') =>
  value.replace(/[^a-zA-Z0-9]/g, '');

const isTaxOfficeIBAN = (iban: string) =>
  ['BE42679200000054', 'BE61679200229117'].includes(
    removeNonAlphanumeric(iban),
  );

const parseJwtToken = (token: string) => {
  const base64Url = token.split('.')[1];

  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );

  return JSON.parse(jsonPayload);
};

const hasCurelyBracesText = (str: string): boolean => /{(.*?)}/.test(str);

const removeSoftHyphens = (text: string): string =>
  text.replace(/[\u00AD\u002D\u2011]+/g, '');

const getLocationOrigin = (updateToHttps = false): string =>
  updateToHttps
    ? location.origin.replace(/^http:\/\//i, 'https://')
    : location.origin;

const caseInsensitiveEqual = (
  a: string | undefined,
  b: string | undefined,
): boolean => a?.toLowerCase() === b?.toLowerCase();

const showPeriodAmount = (returnFrequency: string): boolean =>
  returnFrequency !== 'yearly';

const excludeNumbers = (str?: string): string =>
  str?.replace(/[0-9]/g, '') || '';

export const sanitizeSteuernummer = (steuernummer: string) =>
  (steuernummer || '')
    .replace(/\s/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .trim();

const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const getCustomerIdFromPath = (pathname?: string): string | null => {
  const customerId = (pathname || window.location.pathname)?.split('/')?.[2];

  return customerId?.match(/^[0-9a-fA-F]{24}$/) ? customerId : null;
};
const getCustomerIdFromUrlParams = (): string | null => {
  // Check URL search params (e.g., ?customerId=123abc)
  const urlParams = new URLSearchParams(window.location.search);
  const customerIdFromParams = urlParams.get('customerId');

  return customerIdFromParams?.match(/^[0-9a-fA-F]{24}$/)
    ? customerIdFromParams
    : null;
};

const copy = async (text: string): Promise<boolean> => {
  try {
    if ('clipboard' in navigator) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      return document.execCommand('copy', true, text);
    }
  } catch (error) {
    // we do dynamic import here to avoid circular dependency!
    const { default: showToast } = await import('./toast');
    // Clipboard permission denied - show user-friendly message
    showToast(
      {
        title: i18n.t('clipboard.error.title') as string,
        desc: [i18n.t('clipboard.error.description') as string],
      },
      { type: 'error' },
    );
    return false;
  }
};

const eraseWordsFromString = (text: string, words: string[]): string => {
  let _text = text;

  words.forEach((word: string) => {
    _text = _text.replace(word, '');
  });

  return _text;
};

const getShortAccountType = (accountType: string) =>
  eraseWordsFromString(accountType as string, [
    '_no_vat',
    '_vat',
    '_principal',
    '_student',
    '_complementary',
  ]);

// export const getStatusFromTurnover = ({
//   currentTurnover,
//   projectedTurnover,
//   threshold,
//   previousYearThreshold,
// }: Params) => {
//   if (currentTurnover >= threshold) {
//     return 'error';
//   } else if (
//     projectedTurnover > threshold ||
//     (previousYearThreshold &&
//       currentTurnover >= previousYearThreshold &&
//       currentTurnover < threshold)
//   ) {
//     return 'warning';
//   } else {
//     return 'success';
//   }
// };

const getVATTypeI18nKey = (VATType: any, country: any) => {
  return i18n.t(
    `VATType.${VATType}${
      VATType === VATTypeEnum.franchisee ? `.${country}` : ''
    }`,
  );
};

const getSelectOptionValue = (
  options: OptionType[],
  value: string | number,
): OptionType | undefined =>
  options?.find(({ value: _value }: OptionType) => _value === value);

export const isJointDeclarationEligible = (
  tax: IncomeTaxDE,
  status: CivilStatusEnum,
  civilStatusDate?: Date | null,
): boolean =>
  ([CivilStatusEnum.civilPartnership, CivilStatusEnum.separated].includes(
    status,
  ) &&
    dayjs(civilStatusDate).year() >= tax.period.year) ||
  (CivilStatusEnum.divorced === status &&
    civilStatusDate &&
    dayjs(civilStatusDate) > dayjs('01/01/2021')) ||
  (CivilStatusEnum.married === status &&
    dayjs(civilStatusDate).year() < tax?.period?.year + 1);

const cleanEmptyValues_stringified = (value: any, keep: string[] = []) => {
  return JSON.stringify(value, (k, v) =>
    v == null && !keep.includes(k) ? undefined : v,
  );
};

const cleanEmptyValues = (value: any, keep: string[] = []) => {
  try {
    return JSON.parse(cleanEmptyValues_stringified(value, keep));
  } catch (e) {
    return value;
  }
};

const createSentryCustomErrorMsg = (msg: string) => `CUSTOM:${msg}`;

const openInNewTab = (href: string) => {
  const a = document.createElement('a');
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.href = href;
  a.click();
};

type Options = {
  lowerCaseKeys?: boolean;
};
const resolveImgsContext = (
  imgsContext: __WebpackModuleApi.RequireContext,
  options?: Options,
) => {
  return imgsContext
    .keys()
    .reduce((acc: Dictionary<string>, imgShortPath: string) => {
      const imgName = imgShortPath.replace(/.\/|.png|.svg|.jpg|.jpeg/g, '');
      acc[options?.lowerCaseKeys ? imgName.toLowerCase() : imgName] =
        imgsContext(imgShortPath);
      return acc;
    }, {});
};

const appendQP = (qs: string, qp?: QP) => {
  const ins = new URLSearchParams(qs);
  if (!qp) return ins.toString();
  Object.entries(qp).forEach(([key, val]) => {
    if (val == null) return;
    if (ins.has(key)) ins.delete(key);
    ins.append(key, val + '');
  });
  return ins.toString();
};

const isTester = (user?: IUser) => !!user?.email.includes('@accountable.eu');

const guessIsUserBE = () => {
  try {
    return (
      new Intl.DateTimeFormat().resolvedOptions().timeZone === 'Europe/Brussels'
    );
  } catch (e) {
    return false;
  }
};

const guessIsUserDE = () => {
  try {
    return (
      new Intl.DateTimeFormat().resolvedOptions().timeZone === 'Europe/Berlin'
    );
  } catch (e) {
    return false;
  }
};

export const getIsPastDueDate = (
  taxesByYear: TaxesByYear,
  tab: string,
): boolean =>
  get(taxesByYear, `additional.dueItems.${tab}`)?.some(
    (dueItem: any) =>
      dayjs(get(dueItem, 'dueDate')).diff(dayjs(), 'days') + 1 <= 0,
  );

export const sanitizeIBAN = (iban: string): string =>
  iban
    ?.replace(/\s/g, '')
    ?.replace(/[^a-zA-Z0-9]/g, '')
    ?.trim();
interface Filter {
  key: string;
  options: { value: string }[];
}
const mergeFilters = <T extends Filter>(
  filters: T[] | undefined,
  newFilters: T[],
) => {
  if (!filters || !filters.length) return newFilters;
  const newFiltersMap = keyBy(newFilters, 'key');
  return uniqBy(filters.concat(newFilters), 'key').map((f) => {
    if (!newFiltersMap[f.key]) return f;
    return {
      ...f,
      options: uniqBy(f.options.concat(newFiltersMap[f.key].options), 'value'),
    };
  });
};

const formatLockStatus = (
  taxLock?: LockStatusType,
): LockStatusTypeCatgoriesed => {
  if (!taxLock || !taxLock.length) return null;
  return groupBy(taxLock, 'field') as LockStatusTypeCatgoriesed;
};

const isNonEditableInstallmentInvoice = (invoice: Partial<Invoice>) => {
  if (!invoice._matchedProject) return false;
  const isLastInstallment =
    last(invoice._matchedProject?.revenues)?.revenueId._id === invoice._id;
  return !isLastInstallment;
};

const getNextInstallmentRevenue = (invoice: Partial<Invoice>) => {
  const project = invoice._matchedProject;
  const id = invoice._id;
  if (!project) return null;
  const installmentIndex = project.revenues.findIndex(
    (r) => r.revenueId._id === id,
  );
  return project.revenues[installmentIndex + 1] || null;
};

const isDocumentLockedByTaxes = (
  document: Partial<Invoice> | Partial<IExpenseV2>,
) => {
  return !!document.taxLock?.length;
};

const isDocumentLockedByAccountant = (
  document: Partial<Invoice> | Partial<IExpenseV2>,
) => {
  const isLockedByAccountant =
    isWeb && document.accountantReview?.reviewStatus === 'reviewed';
  return isLockedByAccountant;
};

const isDocumentLockedByTaxesOrAccountant = (
  document: Partial<Invoice> | Partial<IExpenseV2>,
) => {
  const isLockedByAccountant = isDocumentLockedByAccountant(document);
  const isLockedByTaxes = isDocumentLockedByTaxes(document);
  return isLockedByAccountant || isLockedByTaxes;
};
const isEndOrBeginingOfAYear =
  (dayjs().month() === 10 && dayjs().date() >= 29) || // from november 29th
  dayjs().month() === 11 || // all of december
  (dayjs().month() === 0 && dayjs().date() <= 31); // all of january

const isUserEligibleForWrapped = (user?: IUser) => {
  if (!user) return false;

  const now = dayjs();

  return now.month() === 0
    ? dayjs(user.created).year() < now.year() // If we're in January, the user should have signed up in a previous year
    : true; // For November/December, any existing user is eligible
};

const getExpenseReferSlugs = (data: {
  isCreditNote?: boolean;
  country: string;
}) => {
  const { isCreditNote, country } = data;

  return {
    thisType: isCreditNote ? 'credit_note.' + country : 'expense',
    thisTypeAndOtherType:
      (isCreditNote ? 'credit_note_expense.' : 'expense_credit_note.') +
      country,
  };
};

const getRevenuesReferSlugs = (data: { type: string; country: string }) => {
  const { type, country } = data;

  const isCreditNote = type === InvoiceTypeEnum.CREDIT_NOTE;
  const isTicket = type === InvoiceTypeEnum.OTHER_REVENUE;

  return {
    thisType: isCreditNote
      ? 'credit_note.' + country
      : isTicket
      ? 'ticket'
      : 'invoice',
    thisTypeAndOtherType: isCreditNote
      ? 'credit_note_invoice.' + country
      : isTicket
      ? 'ticket_credit_note.' + country
      : 'invoice_credit_note.' + country,
  };
};

const getPromiseHandles = () => {
  let resolve = (value?: unknown) => {};
  let reject = (value?: unknown) => {};

  const promise = new Promise((res, rej) => {
    resolve = res as any;
    reject = rej as any;
  });

  return { promise, resolve, reject };
};

const extractResponseErrors = (error: any): string[] => {
  const errors = get(error, 'response.data.errors');
  if (!Array.isArray(errors)) return [];
  return errors
    .map((e: any) => get(e, 'code'))
    .filter((maybeStr) => typeof maybeStr === 'string' && maybeStr.length > 0);
};

const dateNumberToString = (dateNumber: number, format?: string) =>
  dayjs(`${dateNumber}`, 'YYYYMMDD').format(format || 'YYYY-MM-DD');

const stagingURLs = {
  hydrogen: 'https://hydrogen.web.accountable.eu/',
  hydrogen2: 'https://hydrogen2.web.accountable.eu/',
  helium: 'https://helium.web.accountable.eu/',
  helium2: 'https://helium2.web.accountable.eu/',
  iron: 'https://iron.web.accountable.eu/',
  iron2: 'https://iron2.web.accountable.eu/',
  oxygen: 'https://oxygen.web.accountable.eu/',
  oxygen2: 'https://oxygen2.web.accountable.eu/',
  carbon: 'https://carbon.web.accountable.eu/',
  carbon2: 'https://carbon2.web.accountable.eu/',
  staging: 'https://staging.web.accountable.eu/',
  staging2: 'https://staging2.web.accountable.eu/',
  krypton: 'https://krypton.web.accountable.eu/',
  krypton2: 'https://krypton2.web.accountable.eu/',
  xenon: 'https://xenon.web.accountable.eu/',
  xenon2: 'https://xenon2.web.accountable.eu/',
  qa: 'https://qa.web.accountable.eu/',
  qa2: 'https://qa2.web.accountable.eu/',
};

const createRelativeURLFromOrigin = (options?: {
  params?: Record<string, any>;
  base?: string;
  shouldOverride?: boolean;
}) => {
  const { params, base, shouldOverride } = options || {};

  const url = new URL(base || window.location.href);

  if (!params) return url.href;

  if (shouldOverride) {
    url.search = new URLSearchParams(params).toString();
  } else {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
  }

  return url.href;
};

const getCookie = (key: string) => {
  const b = document.cookie.match('(^|;)\\s*' + key + '\\s*=\\s*([^;]+)');

  return b ? b.pop() : '';
};

const getCountriesBasedOnLanguage = (_lang: AvailableLanguages) => {
  if (AvailableLanguages.en === _lang) {
    return [AvailableCountries.belgium, AvailableCountries.germany];
  }
  if (AvailableLanguages.de === _lang) {
    return [AvailableCountries.germany];
  }
  if ([AvailableLanguages.fr, AvailableLanguages.nl].includes(_lang)) {
    return [AvailableCountries.belgium];
  }
  return [];
};

const getFileNameFromPath = (path: string) => {
  return path.split('/').pop() || '';
};

const getFileExtensionFromPath = (path: string) => {
  return path.split('.').pop() || '';
};

const formatSwift = (swift: Nullable<string>) => {
  const normalizedSwift = swift?.replace(/[^A-Za-z0-9]/gm, '');
  return normalizedSwift
    ? `${normalizedSwift.substring(0, 4)} ${normalizedSwift.substring(
        4,
        6,
      )} ${normalizedSwift.substring(6)}`
    : null;
};
/**
 * Checks if object `a` contains all the properties and values from object `b`
 * Recursively checks nested objects and normalizes null/empty string values
 */
export const isPartOf = (a: any, b: any): boolean => {
  return every(b, (value, key) => {
    const normalizedB = value == null || value === '' ? null : value;
    if (normalizedB == null) return true;
    const aValue = get(a, key);
    const normalizedA = aValue == null || aValue === '' ? null : aValue;
    if (isObject(normalizedB)) return isPartOf(normalizedA, normalizedB);
    return normalizedA === normalizedB;
  });
};

export {
  appendQP,
  appendSearchParamsToURL,
  caseInsensitiveEqual,
  cleanEmptyValues,
  cleanEmptyValues_stringified,
  copy,
  createRelativeURLFromOrigin,
  createSentryCustomErrorMsg,
  dateNumberToString,
  emailRegex,
  eraseWordsFromString,
  excludeNumbers,
  extractResponseErrors,
  formatLockStatus,
  getCookie,
  getCountriesBasedOnLanguage,
  getCustomerIdFromPath,
  getCustomerIdFromUrlParams,
  getExpenseReferSlugs,
  getFileExtensionFromPath,
  isNonEditableInstallmentInvoice,
  getNextInstallmentRevenue,
  getFileNameFromPath,
  getLocationOrigin,
  getPromiseHandles,
  getRevenuesReferSlugs,
  getSelectOptionValue,
  getShortAccountType,
  getVATTypeI18nKey,
  guessIsUserBE,
  guessIsUserDE,
  hasCurelyBracesText,
  isDocumentLockedByAccountant,
  isDocumentLockedByTaxes,
  isDocumentLockedByTaxesOrAccountant,
  isEndOrBeginingOfAYear,
  isTaxOfficeIBAN,
  isTaxOfficeName,
  isTester,
  isUserEligibleForWrapped,
  mergeFilters,
  openInNewTab,
  parseJwtToken,
  removeNonAlphanumeric,
  removeSoftHyphens,
  resolveImgsContext,
  shouldSeeChiftIntegrations,
  showPeriodAmount,
  stagingURLs,
  formatSwift,
};
